import { PivotItem } from "@fluentui/react";
import { Checkbox, DefaultButton, IPersonaProps, MessageBar, MessageBarType, NormalPeoplePicker, Panel, PanelType, Pivot, PrimaryButton, Stack, TextField, Toggle } from "office-ui-fabric-react"
import { useCallback, useEffect, useState, useContext } from "react";
import { saveLocation } from "../../../api/siteslocations";
import { ProfileContext } from "../../../Home";
import { IBookingConstraint } from "../../../types/Booking.types";
import { ILocation } from "../../../types/SiteLocation.types"
import Utils from "../../../utils";
import { RestrictionsSection, RestrictionUpdate } from "./RestrictionsSection";

export interface ILocationFormPanelProps {
    onDismiss: (reloadLocations?: boolean) => void;
    location: Partial<ILocation>;
}

export const LocationFormPanel = (props: ILocationFormPanelProps) => {
    const { location } = props;

    const userContext = useContext(ProfileContext);

    const validForm = (title: string|undefined) => {
        return !(!title || title.trim() === "");
    };

    let initialLocationAdminUsers: IPersonaProps[] = [], initialLocationDeskAccessUsers: IPersonaProps[] = [];
    if (props.location.ResourceAdminUsers) {
        initialLocationAdminUsers = props.location.ResourceAdminUsers.filter((sau) => sau.AccessType === 1).map((user) => ({
            text: user.FullName,
            secondaryText: user.Email,
            data: {
                userId: user.Id
            }
        } as IPersonaProps));
        initialLocationDeskAccessUsers = props.location.ResourceAdminUsers.filter((sau) => sau.AccessType === 2).map((user) => ({
            text: user.FullName,
            secondaryText: user.Email,
            data: {
                userId: user.Id
            }
        } as IPersonaProps));
    }

    // Form variables
    const [ title, setTitle ] = useState<string|undefined>(location.Title);
    const [ buildingNameNo, setBuildingNameNo ] = useState<string|undefined>(location.BuildingNameNo);
        // const [ floor, setFloor ] = useState(location.Floor);

    // Booking constraints
    const [ deskConstraint, setDeskConstraint ] = useState<IBookingConstraint|undefined>(location.DeskBookingConstraint);
    const [ roomConstraint, setRoomConstraint ] = useState<IBookingConstraint|undefined>(location.RoomBookingConstraint);
    const [enableCheckIn, setEnableCheckIn] = useState<boolean | undefined>(location.RoomCheckIns);

    // Booking approval / access
    const [ restrictRoomAccess, setRestrictRoomAccess ] = useState<boolean|undefined>(location.RestrictRoomAccess);
    const [ restrictDeskAccess, setRestrictDeskAccess ] = useState<boolean|undefined>(location.RestrictDeskAccess);
    const [ locationAdmins, setLocationAdmins ] = useState<IPersonaProps[]|undefined>(initialLocationAdminUsers);
    const [ deskAccess, setDeskAccess ] = useState<IPersonaProps[]|undefined>(initialLocationDeskAccessUsers);

    // Include teams in user searches
    const [roomIncludeTeams, setRoomIncludeTeams] = useState<boolean|undefined>(false);
    const [deskIncludeTeams, setDeskIncludeTeams] = useState<boolean|undefined>(false);

    // Form validation
    const [ error, setError ] = useState<string|undefined>();
    const [ formValid, setFormValid ] = useState(validForm(title));

    const getTitleCasedValues = (obj: {[index: string]: any}) => {
        const returnObj: {[index: string]: any} = {};
        for (let key in obj) {
            const titleCasedKey = key.charAt(0).toUpperCase() + key.substr(1);
            returnObj[titleCasedKey] = obj[key]
        }
        return returnObj;
    };

    const _saveForm = async () => {
        const formValues = getTitleCasedValues({ title, buildingNameNo, restrictDeskAccess, restrictRoomAccess /*, floor*/ });
        const siteAdminUserIds = locationAdmins?.filter((sa) => Boolean((sa as any).data?.userId))?.map((sa: any) => sa.data.userId);
        const roomAdminTeamIds = locationAdmins?.filter((sa) => Boolean((sa as any).data?.teamId))?.map((sa: any) => sa.data.teamId);
        const deskAccessUserIds = deskAccess?.filter((da) => Boolean((da as any).data?.userId))?.map((sa: any) => sa.data.userId);
        const deskAccessTeamIds = deskAccess?.filter((da) => Boolean((da as any).data?.teamId))?.map((sa: any) => sa.data.teamId);
        const response = await saveLocation({
            ...location, 
            ...formValues,
            RoomAdminUsers: siteAdminUserIds || [],
            DeskAccessUsers: deskAccessUserIds || [],
            RoomAdminTeams: roomAdminTeamIds || [],
            DeskAccessTeams: deskAccessTeamIds || [],
            BookingConstraints: [deskConstraint, roomConstraint].filter((bc) => Boolean(bc)),
            RoomCheckIns: enableCheckIn
        } as ILocation);
        if (response.ok) {
            if (userContext) {
                if (
                    (siteAdminUserIds && siteAdminUserIds.find((sa) => sa === userContext.Profile.Id)) ||
                    (deskAccessUserIds && deskAccessUserIds.find((sa) => sa === userContext.Profile.Id))
                ) {
                    const abortController = new AbortController();
                    userContext.onProfileUpdated(abortController);
                }
            }
            props.onDismiss(true);
        } else {
            setError("Unable to save location: " + response.statusText);
        }
    };

    useEffect(() => {
        setFormValid(validForm(title));
    }, [title]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mapUserToPersona = useCallback(Utils.mapToPersona, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const roomUserSearch = useCallback(Utils.userSearch(roomIncludeTeams), [mapUserToPersona, roomIncludeTeams]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deskUserSearch = useCallback(Utils.userSearch(deskIncludeTeams), [mapUserToPersona, deskIncludeTeams]);

    return (
        <Panel 
            type={PanelType.medium} 
            isOpen={true} 
            onDismiss={() => props.onDismiss()}
            headerText={location.Id === undefined ? 'New Location' : `Edit Location: ${props.location.Title || ""}`}
            onRenderFooter={() => <Stack horizontal horizontalAlign="end" style={{padding: 20}}>
                <PrimaryButton disabled={!formValid} iconProps={{iconName: "Save"}} text="Save" onClick={() => { _saveForm() }} style={{marginRight: 20}} />
                <DefaultButton iconProps={{iconName: "Cancel"}} text="Cancel" onClick={() => { props.onDismiss(false) }} />
            </Stack>}
        >
            <Pivot>
                <PivotItem key="siteDetails" headerText="Details">
                    { error !== undefined && (
                        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                    )}
                    <TextField autoFocus label="Location Name" value={title} onChange={(e, newValue) => setTitle(newValue)} errorMessage={!formValid ? "Title is mandatory" : undefined} />
                    <TextField label="Building Name or No" value={buildingNameNo} onChange={(e, newValue) => setBuildingNameNo(newValue)} />
                    {/* <TextField label="Floor" value={floor} onChange={(e, newValue) => setFloor(newValue)} /> */}
                </PivotItem>
                <PivotItem key="restrictions" headerText="Restrictions">
                    <RestrictionsSection
                        resourceId={location.Id || -1}
                        onChange={(constraints, checkIn) => {
                            setDeskConstraint(constraints.deskBookingConstraint);
                            setRoomConstraint(constraints.roomBookingConstraint);
                            setEnableCheckIn(checkIn);
                        }}
                        updateType={RestrictionUpdate.Location}
                        deskBookingConstraint={deskConstraint}
                        roomBookingConstraint={roomConstraint}
                        roomCheckIn={enableCheckIn}
                    />
                </PivotItem>
                <PivotItem key="approval" headerText="Approval">
                    <Toggle label="Restrict room booking to Site Admins only" onText="Yes" offText="No" checked={restrictRoomAccess} onChange={(e, checked) => {
                        if (!checked && window.confirm('This will remove all room booking restrictions, allowing all users to book rooms. Restriction settings saved for individual users will be lost. Are you sure?')) setRestrictRoomAccess(checked);
                        if (checked) setRestrictRoomAccess(checked);
                    }} />
                    { restrictRoomAccess === true && (
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}}>
                            <NormalPeoplePicker
                                selectedItems={locationAdmins}
                                onResolveSuggestions={roomUserSearch}
                                onChange={(items) => setLocationAdmins(items)}
                            />
                            <Checkbox label="Include Teams" checked={roomIncludeTeams} onChange={(e, checked) => setRoomIncludeTeams(checked)}/>
                        </Stack>
                    )}
                    <Toggle label="Restrict desk booking to specified users only" onText="Yes" offText="No" checked={restrictDeskAccess} onChange={(e, checked) => {
                        if (!checked && window.confirm('This will remove all desk access restrictions, allowing all users to book desks. Restriction settings saved for individual users will be lost. Are you sure?')) setRestrictDeskAccess(checked);
                        if (checked) setRestrictDeskAccess(checked);
                    }} />
                    { restrictDeskAccess === true && (
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}}>
                            <NormalPeoplePicker
                                selectedItems={deskAccess}
                                onResolveSuggestions={deskUserSearch}
                                onChange={(items) => setDeskAccess(items)}
                            />
                            <Checkbox label="Include Teams" checked={deskIncludeTeams} onChange={(e, checked) => setDeskIncludeTeams(checked)}/>
                        </Stack>
                    )}
                </PivotItem>
            </Pivot>
        </Panel>
    )
}